import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
let scriptPromise;
const loadCheckout = () => {
  if (window.Razorpay) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = resolve; s.onerror = () => reject(new Error("Could not load the payment window"));
      document.body.appendChild(s);
    });
  }
  return scriptPromise;
};

// Card / UPI / net-banking via Razorpay. Renders nothing until the studio's keys are configured.
export const RazorpayPay = ({ booking, api, onPaid }) => {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${api}/api/payments/razorpay/config`).then((r) => r.json()).then((c) => setEnabled(!!c.enabled)).catch(() => {});
  }, [api]);

  if (!enabled) return null;

  const pay = async () => {
    setBusy(true);
    try {
      await loadCheckout();
      const res = await fetch(`${api}/api/payments/razorpay/order`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ booking_id: booking.booking_id }) });
      const order = await res.json();
      if (!res.ok) throw new Error(order.detail || "Could not start the payment");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: order.name, description: order.description, prefill: order.prefill, notes: { booking_id: booking.booking_id },
        theme: { color: "#161614" },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (resp) => {
          try {
            const v = await fetch(`${api}/api/payments/razorpay/verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ booking_id: booking.booking_id, ...resp }) });
            const data = await v.json();
            if (!v.ok) throw new Error(data.detail || "Payment could not be verified");
            onPaid(data);
          } catch (err) {
            toast.error(err.message);
          } finally {
            setBusy(false);
          }
        },
      });
      rzp.on("payment.failed", (e) => { toast.error(e.error?.description || "Payment failed"); setBusy(false); });
      rzp.open();
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-stone-300 p-6 lg:p-8" data-testid="razorpay-pay-card">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#2D6A4F] flex items-center gap-2"><ShieldCheck size={14} /> Instant confirmation</p>
      <h3 className="font-serif text-2xl text-stone-900 mt-3">Pay {inr(booking.pay_now)} online</h3>
      <p className="mt-2 text-sm text-stone-600 font-light leading-relaxed">Cards, UPI, net-banking and wallets through Razorpay. Your receipt is confirmed the moment the payment succeeds — no reference number to type.</p>
      <motion.button whileTap={{ scale: 0.97 }} type="button" onClick={pay} disabled={busy} className="btn-dark mt-6 disabled:opacity-60" data-testid="razorpay-pay-btn">
        {busy ? <span className="blocks" aria-label="Opening payment"><i /><i /><i /></span> : <><CreditCard size={16} /> Pay securely</>}
      </motion.button>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">Secured by Razorpay · or use the UPI QR below</p>
    </div>
  );
};
